import { USER_ROLES } from 'src/_shared/constants/userRolesEnum.js';

// Company JSON columns arrive as strings; tolerate either form.
const parse = (value) => {
	if (typeof value !== 'string') return value;
	try {
		return JSON.parse(value);
	} catch {
		return null;
	}
};

/**
 * Tabs for the mobile shell's bottom bar (ERIN-8561): the old native app's
 * Dashboard / Jobs / Referrals / Bonuses, gated by the same company settings,
 * platform filters and role rules the sidebar and header use. Titles are
 * multilingual keys; the component translates them.
 */
const MAX_TABS = 4;

export const getMobileTabs = (currentUser) => {
	const company = currentUser?.company;
	if (!company) return [];

	const role = currentUser.role;
	const displayAs = currentUser.displayAs || role;
	const featureFlags = currentUser.featureFlags || [];
	const platformConfig = parse(company.platformConfig) || {
		primaryPlatform: 'referrals',
		filterMode: 'exclusionary',
		filterList: [],
	};
	const checkFiltered = (page) =>
		role !== USER_ROLES.SUPER_ADMIN &&
		(platformConfig.filterList || []).includes(page);
	const managerPermissions = parse(company.disableManagerPermissionsByType) || {
		managerReferrals: 'edit',
		managerJobs: 'edit',
		managerBonuses: 'hidden',
		managerEmployees: 'hidden',
	};
	const managerHidden = (key) =>
		displayAs === USER_ROLES.MANAGER && managerPermissions[key] === 'hidden';
	const employeeView = displayAs === USER_ROLES.EMPLOYEE;
	const extendedUser = displayAs === USER_ROLES.EXTENDED_USER;
	const showEmployeePagesForManager =
		featureFlags.includes('showEmployeePagesForManager') &&
		role === USER_ROLES.MANAGER;
	const recognitionSettings = company.recognitionSettings || {};
	// Same rule as the sidebar: admins see the dashboard unless viewing as an
	// employee.
	const adminView =
		(role === USER_ROLES.ADMIN || role === USER_ROLES.SUPER_ADMIN) &&
		displayAs !== USER_ROLES.EMPLOYEE;
	const recHub =
		platformConfig.primaryPlatform === 'recognition' &&
		!recognitionSettings.disableRecHub &&
		!adminView;

	const tabs = [];

	if (recHub) {
		tabs.push({
			key: 'home',
			title: recognitionSettings.recognitionCustomTitle || 'RecHub',
			translate: false,
			link: '/recognition-hub',
			icon: 'dashboard',
		});
	} else if (!checkFiltered('dashboard')) {
		tabs.push({
			key: 'home',
			title: 'Dashboard',
			link: '/dashboard',
			icon: 'dashboard',
		});
	}

	if (
		!checkFiltered('jobs') &&
		!company.hideJobsPage &&
		!(company.disableExtendedUserJobsView && role === USER_ROLES.EXTENDED_USER)
	) {
		tabs.push({
			key: 'jobs',
			title: 'Jobs',
			link: '/browsejobs',
			icon: 'magnifying-glass',
		});
	}

	if (!checkFiltered('referrals') && !extendedUser) {
		if (employeeView || showEmployeePagesForManager) {
			tabs.push({
				key: 'referrals',
				title: 'My Referrals',
				link: '/myreferrals',
				icon: 'group',
			});
		} else if (!managerHidden('managerReferrals')) {
			tabs.push({
				key: 'referrals',
				title: 'Referrals',
				link: '/referrals',
				icon: 'group',
			});
		}
	}

	if (!checkFiltered('bonuses') && !extendedUser) {
		if (employeeView || showEmployeePagesForManager) {
			if (!company.hideBonus) {
				tabs.push({
					key: 'bonuses',
					title: 'My Bonuses',
					link: '/mybonuses',
					icon: 'bonus',
				});
			}
		} else if (!managerHidden('managerBonuses')) {
			tabs.push({
				key: 'bonuses',
				title: 'Bonuses',
				link: '/bonuses',
				icon: 'bonus',
			});
		}
	}

	return tabs.slice(0, MAX_TABS);
};

export const isTabActive = (pathname, link, key) =>
	pathname === link ||
	pathname.startsWith(`${link}/`) ||
	// "/" renders the dashboard without redirecting.
	(key === 'home' && pathname === '/');
